// Filter kolom seragam: token warna (merah/oren/ijo/lime/netral/kuning) +
// kondisi numerik (>=, <=, >, <, =, angka loose-format) + substring teks,
// digabung dengan operator `and` / `or` (WAJIB ada spasi di sisi operator,
// supaya kata seperti "orange" tidak terbaca sebagai "or").

export type ColorCategory = 'merah' | 'oren' | 'ijo' | 'lime' | 'netral' | 'kuning';

const HEX_TO_CAT: Record<string, ColorCategory> = {
  '#ff0000': 'merah',
  '#ffa500': 'oren',
  '#00ff00': 'ijo',
  '#b2ff00': 'lime',
  '#ffff00': 'kuning',
};

const TOKEN_TO_CAT: Record<string, ColorCategory> = {
  merah: 'merah',
  red: 'merah',
  oren: 'oren',
  orange: 'oren',
  ijo: 'ijo',
  hijau: 'ijo',
  green: 'ijo',
  lime: 'lime',
  netral: 'netral',
  neutral: 'netral',
  abu: 'netral',
  gray: 'netral',
  grey: 'netral',
  kuning: 'kuning',
  yellow: 'kuning',
};

// Normalisasi class CSS warna teks → kategori warna.
// null = tidak dikenali (bukan warna yang bisa difilter).
export const colorCategoryFromClass = (cls: string | null | undefined): ColorCategory | null => {
  if (!cls) return null;
  const lower = cls.toLowerCase();
  const hex = lower.match(/#[0-9a-f]{6}|#[0-9a-f]{3}(?![0-9a-f])/);
  if (hex) {
    let h = hex[0];
    if (h.length === 4) h = '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
    const mapped = HEX_TO_CAT[h];
    if (mapped) return mapped;
  }
  if (/\bred\b|text-rose/.test(lower)) return 'merah';
  if (/\bpink\b/.test(lower)) return 'merah';
  if (/\borange\b/.test(lower)) return 'oren';
  if (/\blime\b/.test(lower)) return 'lime';
  if (/\bgreen\b/.test(lower)) return 'ijo';
  if (/\byellow\b|\bamber\b/.test(lower)) return 'kuning';
  if (/gray|slate|zinc|muted|white|foreground/.test(lower)) return 'netral';
  return null;
};

export const colorTokenCategory = (atom: string): ColorCategory | null =>
  TOKEN_TO_CAT[atom.trim().toLowerCase()] ?? null;

export const parseLooseNumber = (raw: string): number => {
  let s = raw.toUpperCase();
  let mult = 1;
  const suf = s.slice(-1);
  if (suf === 'K') { mult = 1e3; s = s.slice(0, -1); }
  else if (suf === 'M') { mult = 1e6; s = s.slice(0, -1); }
  else if (suf === 'B') { mult = 1e9; s = s.slice(0, -1); }
  else if (suf === 'T') { mult = 1e12; s = s.slice(0, -1); }
  const dot = s.lastIndexOf('.');
  const com = s.lastIndexOf(',');
  if (dot >= 0 && com >= 0) {
    if (dot > com) s = s.replace(/,/g, '');
    else s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}([.,]\d{3})+$/.test(s)) {
    s = s.replace(/[.,]/g, '');
  } else {
    s = s.replace(/,/g, '.');
  }
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n * mult;
};

export interface ParsedCondition {
  operator: '>' | '>=' | '<' | '<=' | '=';
  target: number;
  // "=" toleran setengah digit terakhir angka yg diketik ("2.2" → 2.15–2.25),
  // supaya cocok dgn nilai yg tampil di layar, bukan kecocokan float persis.
  epsilon?: number;
}

export const parseNumericCondition = (condition: string): ParsedCondition | null => {
  const trimmed = condition.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(>=|<=|>|<|=)?\s*(-?[\d.,]+(?:[TKMBtkmb])?)\s*%?$/i);
  if (!match) return null;
  const operator = (match[1] || '=') as '>' | '>=' | '<' | '<=' | '=';
  const rawNum = match[2] ?? '';
  const target = parseLooseNumber(rawNum);
  if (isNaN(target)) return null;
  // digit desimal setelah normalisasi loose-format ("2,20" → "2.2" → 1 digit)
  const mantissa = rawNum.replace(/[TKMBtkmb]$/i, '');
  const normalized = String(parseLooseNumber(mantissa));
  const decimals = normalized.includes('.') ? ((normalized.split('.')[1] ?? '').replace(/0+$/, '').length) : 0;
  const suffix = rawNum.match(/[TKMBtkmb]$/i)?.[0].toUpperCase();
  const mult = suffix === 'K' ? 1e3 : suffix === 'M' ? 1e6 : suffix === 'B' ? 1e9 : suffix === 'T' ? 1e12 : 1;
  const epsilon = 0.5 * Math.pow(10, -decimals) * mult;
  return { operator, target, epsilon };
};

export const evaluateCondition = (val: number, cond: ParsedCondition | null): boolean => {
  if (!cond) return true;
  switch (cond.operator) {
    case '>': return val > cond.target;
    case '>=': return val >= cond.target;
    case '<': return val < cond.target;
    case '<=': return val <= cond.target;
    case '=': return cond.epsilon !== undefined
      ? Math.abs(val - cond.target) <= cond.epsilon + Math.max(1e-9, Math.abs(cond.target) * 1e-12)
      : val === cond.target;
    default: return val === cond.target;
  }
};

export interface FilterCell {
  value: number;
  text: string;
  colorClass: string | null;
  isNumeric: boolean;
}

export interface CrossRefCondition {
  operator: '>' | '>=' | '<' | '<=' | '=';
  ref: string;
  arith?: { op: '+' | '-' | '*' | '/'; num: number };
}

// Contoh atom: `>d1`, `>=D1+5K`, `>(D1/2)`, `<(d1*2)`, `<(M1*50%)`.
// refRe harus anchored (mis. /^[cf]*[dw]\d+$/ atau /^(?:\d{6}|m\d{1,2})$/i).
export const parseCrossColumnReference = (
  raw: string,
  refRe: RegExp,
  opts?: { requireParens?: boolean },
): CrossRefCondition | null => {
  const m = raw.trim().match(/^(>=|<=|>|<|=)\s*(\()?([a-z0-9]+)\s*(?:([+\-*/])\s*(-?[\d.,]+(?:[TKMBtkmb])?%?))?\s*(\))?$/i);
  if (!m) return null;
  const hasOpen = Boolean(m[2]);
  const hasClose = Boolean(m[6]);
  if (hasOpen !== hasClose) return null;
  if (opts?.requireParens && !hasOpen) return null;
  const ref = (m[3] ?? '').toLowerCase();
  if (!refRe.test(ref)) return null;
  const operator = (m[1] ?? '=') as CrossRefCondition['operator'];
  if (m[4]) {
    let numRaw = m[5] ?? '';
    let scale = 1;
    if (numRaw.endsWith('%')) {
      numRaw = numRaw.slice(0, -1);
      scale = 100;
    }
    const num = parseLooseNumber(numRaw) / scale;
    if (isNaN(num)) return null;
    if (m[4] === '/' && num === 0) return null;
    return { operator, ref, arith: { op: m[4] as '+' | '-' | '*' | '/', num } };
  }
  return { operator, ref };
};

export const applyCrossColumnTarget = (refVal: number, cond: CrossRefCondition): number => {
  if (!cond.arith) return refVal;
  const { op, num } = cond.arith;
  if (op === '+') return refVal + num;
  if (op === '-') return refVal - num;
  if (op === '*') return refVal * num;
  return refVal / num;
};

export const evaluateCrossColumn = (val: number, refVal: number, cond: CrossRefCondition): boolean => {
  const target = applyCrossColumnTarget(refVal, cond);
  switch (cond.operator) {
    case '>': return val > target;
    case '>=': return val >= target;
    case '<': return val < target;
    case '<=': return val <= target;
    case '=': return val === target;
    default: return false;
  }
};

// Hook utk atom khusus (mis. referensi periode ">d0+5"). Return null = bukan milikmu.
export type AtomEvaluator = (atom: string, cell: FilterCell) => boolean | null;

const evalAtom = (
  rawAtom: string,
  cell: FilterCell,
  customAtom?: AtomEvaluator,
): boolean | null => {
  const atom = rawAtom.trim();
  if (!atom) return null;
  const cat = colorTokenCategory(atom);
  if (cat) return cat === colorCategoryFromClass(cell.colorClass);
  if (customAtom) {
    const r = customAtom(atom, cell);
    if (r !== null) return r;
  }
  if (cell.isNumeric) {
    const cond = parseNumericCondition(atom);
    if (cond) return evaluateCondition(cell.value, cond);
    return null;
  }
  return cell.text.includes(atom.toLowerCase());
};

const combineAnd = (results: (boolean | null)[]): boolean | null => {
  if (results.includes(false)) return false;
  if (results.includes(null)) return null;
  return true;
};

const combineOr = (results: (boolean | null)[]): boolean | null => {
  if (results.includes(true)) return true;
  if (results.includes(null)) return null;
  return false;
};

// Evaluasi ekspresi filter satu kolom.
// Grammar: expr := clause (" or " clause)* ; clause := atom (" and " atom)*
// Operator HARUS diberi spasi di dua sisi ("a and b", "a or b").
// Precedence: and > or. null (atom tak bisa dievaluasi) diabaikan dgn aman;
// hasil akhir null = filter tidak berlaku (lolos semua) — sama dgn perilaku lama.
export const evaluateFilterExpression = (
  expr: string,
  cell: FilterCell,
  opts?: { customAtom?: AtomEvaluator },
): boolean => {
  const trimmed = expr.trim();
  if (!trimmed) return true;
  const orClauses = trimmed.split(/\s+or\s+/i);
  const orResults = orClauses.map(clause => {
    const atoms = clause.split(/\s+and\s+/i);
    return combineAnd(atoms.map(a => evalAtom(a, cell, opts?.customAtom)));
  });
  const result = combineOr(orResults);
  return result === null ? true : result;
};
